import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import type { CreateLockResponse } from '@apartsales/shared';

import { ApiCallError, lockApi } from '~/lib/api';

export const Route = createFileRoute('/lock/new')({
  component: BuyerJourney,
  validateSearch: (search) => ({
    unit_id: typeof search.unit_id === 'string' ? search.unit_id : undefined,
  }),
});

type Step = 'details' | 'otp' | 'success';

interface FormState {
  unit_id: string;
  phone: string;
  name: string;
  email: string;
}

function BuyerJourney() {
  const navigate = useNavigate();
  const { unit_id: prefillUnitId } = Route.useSearch();

  const [step, setStep] = useState<Step>('details');
  const [form, setForm] = useState<FormState>({
    unit_id: prefillUnitId ?? '',
    phone: '',
    name: '',
    email: '',
  });
  const [created, setCreated] = useState<CreateLockResponse | null>(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);

  const createMut = useMutation<CreateLockResponse, ApiCallError>({
    mutationFn: () =>
      lockApi.create({
        unit_id: form.unit_id.trim(),
        phone: form.phone.trim(),
        name: form.name.trim(),
        email: form.email.trim() || null,
        source: 'web',
      }),
    onSuccess: (data) => {
      setCreated(data);
      setStep('otp');
    },
  });

  const verifyMut = useMutation<{ status: 'sms_verified' }, ApiCallError, string>({
    mutationFn: async (code) => {
      if (!created) throw new ApiCallError('No active lock', 400, 'no_lock');
      return lockApi.verify(created.lock_id, code);
    },
    onSuccess: () => {
      setOtpError(null);
      setStep('success');
    },
    onError: (err) => {
      const left = (err.extra as { attempts_left?: number } | undefined)?.attempts_left;
      if (err.code === 'invalid_code') {
        setOtpError(
          left !== undefined
            ? `Неверный код. Осталось попыток: ${left}.`
            : 'Неверный код.',
        );
      } else if (err.code === 'too_many_attempts') {
        setOtpError('Слишком много попыток. Lock отменён — создайте новый.');
      } else {
        setOtpError(err.message);
      }
    },
  });

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg text-label">
      {/* Atmospheric backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute left-1/2 top-[-15%] h-[700px] w-[700px] -translate-x-1/2 rounded-full blur-[140px]"
          style={{ backgroundColor: 'rgba(42,150,251,0.16)' }}
        />
      </div>

      <main className="mx-auto flex min-h-screen max-w-md flex-col px-5 py-10">
        <Stepper current={step} />

        <div className="mt-10">
          <AnimatePresence mode="wait">
            {step === 'details' && (
              <motion.section
                key="details"
                {...stepMotion}
                aria-labelledby="step-details-title"
              >
                <h1
                  id="step-details-title"
                  className="text-balance text-[28px] font-semibold leading-tight tracking-tight"
                >
                  Зафиксировать квартиру
                </h1>
                <p className="mt-3 text-[15px] text-label-2">
                  Lock 🔒 закрепляет лот за вами на 30 дней. Юридически — через
                  агентский договор. Поведенчески — 1% сертификат на скидку при сделке.
                </p>

                <form
                  className="mt-7 space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    createMut.mutate();
                  }}
                >
                  <Field
                    label="ID лота"
                    value={form.unit_id}
                    onChange={(v) => setForm({ ...form, unit_id: v })}
                    placeholder="2c25b3e6-f3e2-40ff-811b-18e39f2fb1df"
                    mono
                    autoFocus={!prefillUnitId}
                    required
                  />
                  <Field
                    label="Имя и фамилия"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    placeholder="Иван Иванов"
                    required
                  />
                  <Field
                    label="Телефон"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    placeholder="+7 999 123 45 67"
                    inputMode="tel"
                    required
                  />
                  <Field
                    label="Email (необязательно)"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    placeholder="ivan@example.com"
                    inputMode="email"
                  />

                  {createMut.error && (
                    <ErrorBanner error={createMut.error} />
                  )}

                  <PrimaryButton
                    type="submit"
                    loading={createMut.isPending}
                    disabled={
                      !form.unit_id || !form.name || !form.phone || createMut.isPending
                    }
                  >
                    Получить SMS-код
                  </PrimaryButton>

                  <p className="pt-2 text-center text-[11px] leading-relaxed text-label-3">
                    Нажимая кнопку, вы соглашаетесь с условиями агентского договора и
                    обработкой персональных данных.
                  </p>
                </form>
              </motion.section>
            )}

            {step === 'otp' && created && (
              <motion.section
                key="otp"
                {...stepMotion}
                aria-labelledby="step-otp-title"
              >
                <h1
                  id="step-otp-title"
                  className="text-[28px] font-semibold leading-tight tracking-tight"
                >
                  Введите код из SMS
                </h1>
                <p className="mt-3 text-[15px] text-label-2">
                  Отправили 6-значный код на{' '}
                  <span className="font-mono text-label">{maskPhone(form.phone)}</span>.
                </p>

                {created.otp_dev_hint && (
                  <div
                    className="mt-5 rounded-2xl px-4 py-3 text-[12px]"
                    style={{
                      backgroundColor: 'rgba(255,159,10,0.10)',
                      border: '1px solid rgba(255,159,10,0.25)',
                      color: '#ffb84a',
                    }}
                  >
                    <span className="font-medium">DEV: </span>
                    SMS не отправляется на staging. Код для теста:{' '}
                    <span className="font-mono">{created.otp_dev_hint}</span>
                  </div>
                )}

                <form
                  className="mt-7 space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (otp.length === 6) verifyMut.mutate(otp);
                  }}
                >
                  <OtpInput value={otp} onChange={setOtp} />
                  {otpError && (
                    <p className="text-center text-[13px] text-red-400">{otpError}</p>
                  )}

                  <PrimaryButton
                    type="submit"
                    loading={verifyMut.isPending}
                    disabled={otp.length !== 6 || verifyMut.isPending}
                  >
                    Подтвердить
                  </PrimaryButton>

                  <button
                    type="button"
                    className="block w-full pt-1 text-center text-[13px] text-label-2 transition-colors hover:text-label"
                    onClick={() => {
                      setOtp('');
                      setOtpError(null);
                      setStep('details');
                    }}
                  >
                    ← Изменить данные
                  </button>
                </form>
              </motion.section>
            )}

            {step === 'success' && created && (
              <motion.section
                key="success"
                {...stepMotion}
                aria-labelledby="step-success-title"
              >
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.05,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: 'rgba(48,209,88,0.16)',
                    border: '1px solid rgba(48,209,88,0.40)',
                  }}
                  aria-hidden
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12.5l4.5 4.5L19 7"
                      stroke="#30d158"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>

                <h1
                  id="step-success-title"
                  className="mt-6 text-center text-[28px] font-semibold leading-tight tracking-tight"
                >
                  Lock активирован
                </h1>
                <p className="mt-3 text-center text-[15px] text-label-2">
                  Лот закреплён за вами на 30 дней.
                  Сохраните код или ссылку — она работает и для застройщика.
                </p>

                <div
                  className="mt-7 rounded-2xl border px-5 py-4 text-center"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    borderColor: 'rgba(255,255,255,0.10)',
                  }}
                >
                  <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-label-3">
                    Lock code
                  </p>
                  <p className="mt-1.5 font-mono text-[26px] font-semibold tracking-[0.06em]">
                    {created.code}
                  </p>
                </div>

                <PrimaryButton
                  className="mt-7"
                  onClick={() =>
                    navigate({ to: '/lock/$code', params: { code: created.code } })
                  }
                >
                  Открыть статус →
                </PrimaryButton>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

const stepMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: [0.2, 0.8, 0.2, 1] as [number, number, number, number] },
};

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

function Stepper({ current }: { current: Step }) {
  const order: Step[] = ['details', 'otp', 'success'];
  const idx = order.indexOf(current);
  return (
    <ol className="flex items-center gap-3" aria-label="Прогресс">
      {order.map((s, i) => {
        const reached = i <= idx;
        return (
          <li
            key={s}
            className="h-1 flex-1 rounded-full transition-colors duration-500"
            style={{
              backgroundColor: reached
                ? 'rgba(42,150,251,0.85)'
                : 'rgba(255,255,255,0.10)',
            }}
            aria-current={i === idx ? 'step' : undefined}
          />
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  autoFocus,
  required,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'tel' | 'email';
  autoFocus?: boolean;
  required?: boolean;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium uppercase tracking-[0.14em] text-label-3">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoFocus={autoFocus}
        required={required}
        className={
          'mt-2 w-full rounded-2xl border px-4 py-3 text-[15px] text-label outline-none transition-colors placeholder:text-label-3 focus:border-brand-blue ' +
          (mono ? 'font-mono ' : '')
        }
        style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          borderColor: 'rgba(255,255,255,0.10)',
        }}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// 6-digit OTP input (auto-advances + backspace handling)
// ---------------------------------------------------------------------------

function OtpInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const digits = value.padEnd(6, ' ').slice(0, 6).split('');
  const handleKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx].trim()) {
      const prev = e.currentTarget.previousElementSibling;
      if (prev instanceof HTMLInputElement) prev.focus();
    }
  };
  const handleChange = (idx: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = digits.slice();
    next[idx] = digit || ' ';
    const joined = next.join('').replace(/\s+$/g, '');
    onChange(joined);
    if (digit) {
      const el = document.querySelectorAll<HTMLInputElement>('[data-otp-cell]')[idx + 1];
      el?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const txt = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (txt.length) {
      e.preventDefault();
      onChange(txt);
      const el = document.querySelectorAll<HTMLInputElement>('[data-otp-cell]')[
        Math.min(txt.length, 5)
      ];
      el?.focus();
    }
  };

  return (
    <div className="flex justify-between gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          data-otp-cell
          inputMode="numeric"
          maxLength={1}
          value={d.trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          autoFocus={i === 0}
          className="h-14 w-12 rounded-xl border text-center font-mono text-2xl text-label outline-none transition-colors focus:border-brand-blue"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderColor: 'rgba(255,255,255,0.10)',
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable bits
// ---------------------------------------------------------------------------

function PrimaryButton({
  children,
  loading,
  disabled,
  type,
  onClick,
  className = '',
}: {
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type={type ?? 'button'}
      onClick={onClick}
      disabled={disabled}
      className={
        'block w-full rounded-2xl px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_8px_30px_-8px_rgba(42,150,251,0.6)] transition-all duration-300 active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-50 ' +
        className
      }
      style={{
        background: disabled
          ? 'rgba(255,255,255,0.10)'
          : 'linear-gradient(180deg, #3aa6ff 0%, #2a96fb 100%)',
      }}
    >
      {loading ? 'Подождите…' : children}
    </button>
  );
}

function ErrorBanner({ error }: { error: ApiCallError }) {
  const message =
    error.code === 'unit_not_found'
      ? 'Лот с таким ID не найден.'
      : error.code === 'unit_not_available'
        ? 'Этот лот уже недоступен (продан или забронирован).'
        : error.code === 'unit_already_locked'
          ? 'Этот лот уже зафиксирован другим покупателем.'
          : error.code === 'already_locked'
            ? 'Вы уже создавали Lock на этот лот — проверьте SMS.'
            : error.code === 'invalid_phone'
              ? 'Неверный формат телефона.'
              : error.code === 'rate_limited'
                ? 'Слишком много попыток. Попробуйте через 10 минут.'
                : error.message;
  return (
    <div
      className="rounded-2xl px-4 py-3 text-[13px]"
      style={{
        backgroundColor: 'rgba(255,69,58,0.08)',
        border: '1px solid rgba(255,69,58,0.25)',
        color: '#ff6b62',
      }}
      role="alert"
    >
      {message}
    </div>
  );
}

function maskPhone(p: string): string {
  const digits = p.replace(/\D/g, '');
  if (digits.length < 7) return p;
  return '+' + digits.slice(0, 3) + ' ••• ••' + digits.slice(-2);
}
